import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges } from '@angular/core';
import { IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { ImageModule } from '@df/module-image/image.module';
import { SocialService } from '@df/ui/social/social.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { copyToClipboard } from '@df/util/fn/copy-to-clipboard';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsPageType } from '@icms/core/content/page/icms-page-type.decorator';
import { ICmsDataChangeService } from '@icms/core/data/change/icms-data-change.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { PageViewAbstract } from '../../../../page-view.abstract';
import { LocalPage } from '../../../page.decorator';
import { NBlogCategoryPageType } from '../category-view/cms-blog-page-category-view.component';
import { NBlogRootPageType } from '../root-view/cms-blog-root-page.namespace';
import { NBlogPostPageType } from './cms-blog-post.namespace';
declare module '@df/catalog/cms/cms-model.interface' {
    export interface IPageDataV3 {
        components: NICmsContentComponent.IData[];
    }
}

@ICmsPageType({
    id: NBlogPostPageType.typeId,
    title: 'Blog post',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'typeData.author',
            title: 'Post author'
        },
        {
            type: NICmsContentField.EFieldType.date,
            key: 'typeData.date',
            title: 'Post date'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'typeData.reading_time',
            title: 'Reading time',
            default: '3m'
        },
        <NICmsContentField.IRelationField>{
            key: 'typeData.tags',
            type: NICmsContentField.EFieldType.relation,
            title: 'Additional blog tags',
            options: {
                title: 'Page',
                type: 'page',
                multiple: true,
                filter: (model: ICmsPage) => model.rawData?.pageType === NBlogCategoryPageType.typeId
            },
            multiple: true
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'typeData.top_image',
            title: 'Top image'
        }
    ],
    /**
     * Only allow parent page to be blog-category
     */
    parentFilter: page => {
        const pageType = page.rawData?.pageType;
        return pageType ? [NBlogCategoryPageType.typeId, NBlogRootPageType.typeId].includes(pageType) : false;
    }
})
@Component({
    selector: 'cms-blog-page-post-view',
    templateUrl: './cms-blog-page-post-view.html',
    standalone: true,
    imports: [TranslationModule, ICmsClientModule, ImageModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsBlogPagePostViewComponent extends PageViewAbstract implements OnChanges {
    /**
     * Blog tag categories
     */
    tagCategories: LocalPage<IPageDataV2>[] = [];

    constructor(
        iCmsDataChangeService: ICmsDataChangeService,
        private socialService: SocialService
    ) {
        super(iCmsDataChangeService);
    }

    fbShare() {
        if (this.model) {
            return this.socialService.fbShare(this.model);
        }
    }

    twShare() {
        if (this.model) {
            return this.socialService.twShare(this.model);
        }
    }

    pinShare() {
        if (this.model) {
            return this.socialService.pinShare(this.model);
        }
    }

    copyUrlToClipboard() {
        copyToClipboard(window.location.href);
    }

    protected get typeData() {
        return this.model?.data?.typeData;
    }

    protected get image(): string {
        return this.model?.data?.image || '';
    }

    protected get components(): NICmsContentComponent.IData[] {
        return this.model?.data?.components || [];
    }

    protected get name(): string {
        return this.model?.data?.name || '';
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['model']) {
            this.tagCategories = (this.model as LocalPage<IPageDataV3>)?.getBlogPostCategories()?.slice(0, 3) || [];
            super.ngOnChanges(changes);
        }
    }
}
